<template>
    <div class="container">
        <h1 class="title">
            Profitable acquisitions
        </h1>
        <h2 class="subtitle">
            A list of methods that can be used to gain gold
        </h2>

        <EconomicsWarning/>

        <div v-if="error">
            <b-notification type="is-danger" has-icon :closable="false">
                {{error}}
            </b-notification>
        </div>

        <b-tabs>
            <b-tab-item label="Search">
                <div class="panel-block">
                    <b-field label="How to buy materials?"
                             message="Select the method that will be used to buy all materials that are best bought from the Trading Post.">
                        <b-field>
                            <b-radio-button v-model="buyType" native-value="instant" :disabled="!instantBuyAllowed">Instant buy</b-radio-button>
                            <b-radio-button v-model="buyType" native-value="buyorder" :disabled="!buyOrderAllowed">List buy orders</b-radio-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block">
                    <b-field label="How to sell crafted items?"
                             message="Select the method that will be used to sell all products.">
                        <b-field>
                            <b-radio-button v-model="sellType" native-value="instant" :disabled="!instantSellAllowed">Instant sell</b-radio-button>
                            <b-radio-button v-model="sellType" type="is-danger" native-value="sellorder" :disabled="!sellOrderAllowed"><b-icon icon="clock-outline"/>List sell orders</b-radio-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block">
                    <b-field label="Allow time-gated recipes?"
                             message="Some time-gated recipes are very slow to calculate and you can typically only craft one a day.">
                        <b-field>
                            <b-radio-button v-model="timegated" :native-value="false">No</b-radio-button>
                            <b-radio-button v-model="timegated" type="is-warning" :native-value="true"><b-icon icon="clock-outline" />Yes</b-radio-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block">
                    <b-field label="Allow splitting merchant recipes?"
                             message="TODO: Implement and explain.">
                        <b-field>
                            <b-radio-button disabled>No</b-radio-button>
                            <b-radio-button disabled>Yes</b-radio-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block">
                    <b-field label="Currencies"
                             message="Allowed currencies. These will be spent in any amount in order to maximize gold profit.">
                        <b-field>
                            <b-checkbox-button :key="index" v-for="(currency, index) in availableCurrencies"
                                               v-model="currencies"
                                               :native-value="currency"
                                               :disabled="currency === 'Coppers'"
                            >
                                <img class="image is-24x24" :src="currencyIcons[currency]" :alt="currency">
                                {{currency === 'Coppers' ? 'Gold' : currency}}
                            </b-checkbox-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block">
                    <b-field label="Calculate max profit?"
                             message="Calculating total profit for all items is slow, if you only want to select multiple recipes to craft, this is not needed.">
                        <b-field>
                            <b-radio-button v-model="full" :native-value="false">No</b-radio-button>
                            <b-radio-button v-model="full" type="is-warning" :native-value="true"><b-icon icon="clock-outline"/>Yes</b-radio-button>
                        </b-field>
                    </b-field>
                </div>
                <div class="panel-block is-vertical">
                    <b-button type="is-primary" @click="clickCalculate" :disabled="loading">Calculate</b-button>
                </div>
            </b-tab-item>
            <b-tab-item label="Results" :disabled="!loading && acquisitions === []">

                <b-button icon-left="anvil" type="is-primary" v-if="loaded"
                          @click="loadMultiAcquisition" :disabled="!multiCraftingAllowed || this.multiLoading">
                    Craft all checked rows
                </b-button>

                <b-table
                        :data="acquisitions"
                        :loading="loading"
                        striped
                        narrowed
                        :default-sort="['inventoryDifference', 'desc']"
                        detailed
                        :checked-rows.sync="checkedRows"
                        checkable
                        checkbox-position="right"
                >
                    <template slot-scope="props">
                        <b-table-column field="items[0].iconUrl" label="Icon">
                            <ResourceImage class="is-32x32" :resource="props.row.items[0]"/>
                        </b-table-column>
                        <b-table-column field="items[0].name" label="Item name" sortable>
                            {{ props.row.items[0].name }}
                        </b-table-column>
                        <b-table-column field="inventoryDifference" :custom-sort="sortProfit" label="Profit" sortable>
                            <template v-if="!full">≥ </template>
                            <currency-amount v-bind:key="index"
                                             v-for="(resourceAmount, index) in props.row.inventoryDifference"
                                             :amount="resourceAmount.amount"
                                             :currency="resourceAmount.resource.name"
                            />
                        </b-table-column>
                        <b-table-column field="acquisitionCount" label="Craft count" sortable>
                            <template v-if="!full">≥ </template>
                            {{ props.row.acquisitionCount }}
                        </b-table-column>
                    </template>
                    <template slot="detail" slot-scope="props">
                        <AcquisitionCard :acquisition="props.row" />
                    </template>
                </b-table>
            </b-tab-item>

            <b-tab-item label="Batch crafting" :disabled="!multiLoading && multiAcquisition == null">
                <div v-if="multiAcquisition != null">
                    <AcquisitionCard :acquisition="multiAcquisition" />
                </div>
            </b-tab-item>
        </b-tabs>
    </div>
</template>

<script>
    import axios from 'axios'
    import _ from 'lodash'
    import AcquisitionCard from "@/components/AcquisitionCard";
    import EconomicsWarning from "@/components/EconomicsWarning";
    import ResourceImage from "@/components/ResourceImage";
    import CurrencyAmount from "@/components/CurrencyAmount";
    import Currencies from "@/currencies"

    export default {
        name: 'recipe-list',
        components: {
            ResourceImage,
            EconomicsWarning,
            CurrencyAmount,
            AcquisitionCard
        },
        data() {
            return {
                acquisitions: [],
                loading: false,
                error: "",
                buyType: 'instant',
                sellType: 'instant',
                full: true,
                timegated: false,
                loaded: false,
                checkedRows: [],
                multiAcquisition: null,
                multiLoading: false,
                currencyIcons: Currencies.currencyIcons,
                availableCurrencies: Currencies.availableCurrencies,
                currencies: ["Coppers"]
            }
        },
        computed: {
            multiCraftingAllowed() {
                return this.$store.state.permissions.permissions.includes("EconomicsAcquisitionMultipleItems");
            },
            buyOrderAllowed() {
                return this.$store.state.permissions.permissions.includes("EconomicsAcquisitionBuyOrder");
            },
            instantBuyAllowed() {
                return this.$store.state.permissions.permissions.includes("EconomicsAcquisitionInstantBuy");
            },
            instantSellAllowed() {
                return this.$store.state.permissions.permissions.includes("EconomicsAcquisitionInstantSell");
            },
            sellOrderAllowed() {
                return false;
                //return this.$store.state.permissions.permissions.includes("EconomicsAcquisitionSellOrder");
            },
        },
        methods: {
            loadAsyncData() {
                this.loading = true;
                axios.get("/economics/acquisitions/", {
                    params: {
                        full: this.full,
                        buyType: this.buyType,
                        sellType: this.sellType,
                        timegated: this.timegated,
                        infiniteCurrencies: this.currencies.join(","),
                    }
                }).then(response =>
                {
                    this.acquisitions = response.data;
                    this.error = "";
                    this.loaded = true;
                }).catch(error => {
                    this.acquisitions = [];
                    this.error = error;
                }).finally(() => {
                    this.loading = false;
                })
            },
            loadMultiAcquisition() {
                let itemIds = _.map(this.checkedRows, x => x.items[0].id);
                this.multiLoading = true;
                axios.get("/economics/acquisitions/", {
                    params: {
                        full: this.full,
                        buyType: this.buyType,
                        sellType: this.sellType,
                        timegated: this.timegated,
                        items: itemIds.join(","),
                        infiniteCurrencies: this.currencies.join(","),
                    }
                }).then(response =>
                {
                    this.multiAcquisition = response.data;
                    this.error = "";
                }).catch(error => {
                    this.multiAcquisition = false;
                    this.error = error;
                }).finally(() => {
                    this.multiLoading = false;
                })
            },
            clickCalculate() {
                this.loadAsyncData();
            },
            sortProfit(acquisition1, acquisition2, isAsc) {
                let result = 0;
                let profit1 = acquisition1.inventoryDifference;
                let profit2 = acquisition2.inventoryDifference;
                let coppers1 = _.find(profit1, x => x.resource.name === 'Coppers');
                let coppers2 = _.find(profit2, x => x.resource.name === 'Coppers');
                if (coppers1 && coppers2) {
                    result = coppers1.amount - coppers2.amount;
                } else if (coppers1) {
                    result = 1;
                } else if (coppers2) {
                    result = -1;
                } else {
                    result = _.sumBy(profit1, x => x.amount) - _.sumBy(profit2, x => x.amount);
                }
                return isAsc ? result : -result;
            }
        },
    }
</script>
